<template>
  <div class="w-full shadow-md" :class="[cartDealSettings.useAlternativeStyle ? 'bg-putty' : 'bg-white']">
    <div class="fluid-sm leading-tight bg-br-green-light text-black py-1 text-center">
      <TagIcon v-if="cartDealSettings.showIcon" class="w-3 h-3 inline-block mr-1 text-black" />
      <span class="font-medium">{{ $t('cart.cartDealsHeading') }}</span>
    </div>

    <div class="flex items-center w-full py-2 px-4">
      <div class="w-full max-w-[82px] sm:max-w-[112px] relative mt-2">
        <NuxtLink :to="productLink">
          <FixedAspect v-if="dynamicImage" :ratio="2 / 3">
            <CldnImg
              :src="dynamicImage"
              :alt="title"
              :quality="90"
              crop="fit"
              sizes="20vw sm:10vw md:10vw lg:10vw xl:10vw 2xl:10vw"
            />
          </FixedAspect>
          <FixedAspect v-else :ratio="2 / 3">
            <div
              class="flex items-center justify-center w-full h-full bg-gray-200 text-gray-800 text-xs text-center p-1"
            >
              {{ $t('gallery.no-images') }}
            </div>
          </FixedAspect>
        </NuxtLink>

        <div
          v-if="discountAnnotation"
          class="absolute bg-br-red fluid-xs py-px px-3 top-0 left-0 text-white font-bold whitespace-nowrap"
        >
          - {{ discountAnnotation }}
        </div>
      </div>

      <div class="ml-2 w-full text-sm">
        <NuxtLink :to="productLink" class="block font-medium overflow-hidden -mb-1">
          {{ title }}
        </NuxtLink>
        <div class="flex flex-no-wrap items-baseline justify-between w-full space-x-2">
          <div class="leading-tightest w-full">
            <FormSelectInput
              v-if="hasMultipleVariants"
              :model-value="variant?.id"
              :options="variantOptions"
              appearance="light"
              :show-errors="false"
              :show-label="false"
              text-center
              @update:modelValue="handleVariantChange"
            />
            <div v-else-if="variant" class="text-gray-light">
              {{ variant.title }}
            </div>
          </div>
          <div class="text-right pr-2">
            <ProductPriceLine
              v-if="calculatedPrice.amount > 0"
              v-bind="{
                price: calculatedPrice,
                showVatHint: false,
                showCentered: false,
                showQuantity: true,
                unitPrice,
                unitPriceMeasurement,
                size: 'xs',
              }"
            />
            <span v-else class="text-br-green text-xs font-medium uppercase">{{ $t('common.forFree') }}</span>
          </div>
          <CtaButton
            class="p-3 shrink-0 rounded-full min-w-0"
            data-testid="smBtnAddToCart"
            :loading="isLoading"
            @click.native="addDealToCart"
            :title="$t('cart.addToCart')"
          >
            <PlusIcon class="h-4 w-4" />
          </CtaButton>
        </div>
      </div>
    </div>
  </div>
</template>

<script setup>
import { toRaw, computed, ref } from 'vue';
import FixedAspect from '@/components/common/FixedAspect';
import CldnImg from '@/components/CldnImg';
import ProductPriceLine from '@/components/shop/products/ProductPriceLine';
import PlusIcon from '@/components/icons/PlusIcon';
import CtaButton from '@/components/common/CtaButton';
import TagIcon from '@/components/icons/TagIcon';
import FormSelectInput from '~/components/account/FormSelectInput.vue';
import * as Sentry from '@sentry/vue';
import { usePickystoryDeal } from '~/composables/usePickystoryDeal';
import { useCartStore } from '@/stores/cart';
import { useCartSettingsStore } from '@/stores/cart-settings';
import { storeToRefs } from 'pinia';

const props = defineProps({
  deal: { type: Object, required: true },
  title: { type: String, default: null },
  description: { type: String, default: null },
  image: { type: String, default: null },
  unitPrice: { type: Object, default: () => ({}) },
  unitPriceMeasurement: { type: Object, default: () => ({}) },
  handle: { type: String, default: null },
});

const { $localeRootPath, $dlPush } = useNuxtApp();
const { n: $n } = useI18n();

// Store setup
const cartStore = useCartStore();
const cartSettingsStore = useCartSettingsStore();
const { lineItems } = storeToRefs(cartStore);
const { cartDealSettings } = storeToRefs(cartSettingsStore);

const isLoading = ref(false);

// Convert props.deal to ref for composable
const dealRef = computed(() => props.deal);

// Use composable with cart line items
const { bundleCompareAtPrice, productsList, updateVariant } = usePickystoryDeal(dealRef, {
  cartLineItems: lineItems,
});

const productsListFromComposable = productsList;

// Computed properties
const priceWithoutDiscount = computed(() => {
  return bundleCompareAtPrice.value?.amount || 0;
});

// Get offer product from composable productsList
const offerProductFromComposable = computed(() => {
  if (!props.deal?.offerProduct || !productsListFromComposable.value) return null;
  return productsListFromComposable.value.find((p) => p.id === props.deal.offerProduct.id);
});

const variant = computed(() => {
  // Use selected variant from composable (already handles cart detection)
  return offerProductFromComposable.value?.selected || null;
});

const calculatedPrice = computed(() => {
  // Use displayPrice from composable with fallback to first variant price
  return (
    offerProductFromComposable.value?.displayPrice ||
    props.deal?.offerProduct?.variants?.[0]?.price || { amount: 0, currencyCode: 'EUR' }
  );
});

const discountAnnotation = computed(() => {
  if (!props.deal.discountValue) {
    return false;
  }

  const formatter = (amount) => {
    return $n(parseFloat(amount), {
      style: 'currency',
      currency: calculatedPrice.value.currencyCode,
    });
  };

  if (props.deal.discountType === 'percentage') {
    return `${props.deal.discountValue}%`;
  } else if (props.deal.discountType === 'fixedAmount') {
    const discount = formatter(props.deal.discountValue);
    return `${discount}`;
  } else if (props.deal.discountType === 'fixedTargetPrice') {
    const discountAmount = Math.round((priceWithoutDiscount.value - props.deal.discountValue) * 100) / 100;
    const discount = formatter(discountAmount);

    return discount;
  } else {
    return false;
  }
});

const productLink = computed(() => {
  return props.handle ? $localeRootPath(`/products/${props.handle}`) : null;
});

const variants = computed(() => {
  return props.deal?.offerProduct?.variants || [];
});

const hasMultipleVariants = computed(() => {
  return variants.value.length > 1;
});

const variantOptions = computed(() => {
  return variants.value.map((v) => ({
    label: v.title,
    value: v.id,
    disabled: !v.available,
  }));
});

const dynamicImage = computed(() => {
  // Use selected variant's image if available, otherwise fall back to prop image
  return variant.value?.image || props.image;
});

// Methods
const close = () => cartStore.close();

const handleVariantChange = (variantId) => {
  if (props.deal?.offerProduct?.id) {
    updateVariant(props.deal.offerProduct.id, variantId);
  }
};

const addDealToCart = async () => {
  // Cart deals show in the drawer once one bundle product is already in the
  // cart, so we only add the products that are still missing.
  const cartSkus = new Set(lineItems.value.map((l) => l.variant.sku));
  const resolveVariant = (product) =>
    productsListFromComposable.value.find((p) => p.id === product.id)?.selected || product.variants[0];

  let pos = 0;
  const buildPayload = (sourceProducts) =>
    sourceProducts
      .map(resolveVariant)
      .filter((variant) => variant && !cartSkus.has(variant.sku))
      .map((variant) => ({ id: variant.id, position: ++pos }));

  const products = buildPayload(props.deal.products);
  const addonProducts = props.deal.isBogo ? buildPayload(props.deal.discountProducts || []) : null;

  if (products.length === 0 && (!addonProducts || addonProducts.length === 0)) {
    return;
  }

  isLoading.value = true;
  try {
    const rawDeal = toRaw(props.deal.pickystory);
    await rawDeal.addVariantsToCart(products, 1, addonProducts);
    await cartStore.pickystoryRefreshCart();

    // Isolate GTM so a tracking error never blocks the cart flow.
    try {
      const addedVariantIds = new Set([...products, ...(addonProducts || [])].map((p) => p.id));
      const variantPairs = productsListFromComposable.value
        .filter((product) => addedVariantIds.has(product.selected.id))
        .map((product) => ({
          variant: {
            ...product.selected,
            price: { ...product.selected.price, amount: product.exactDiscountedPrice },
          },
          product,
        }));

      if (variantPairs.length > 0) {
        $dlPush.eECaddToCart(variantPairs, 1);
      }
    } catch (trackingError) {
      console.error('[CartDealBundle] GTM tracking failed', trackingError);
      Sentry.captureException(trackingError);
    }
  } catch (error) {
    console.error('[CartDealBundle] addDealToCart failed', error);
    Sentry.captureException(error);
  } finally {
    await cartStore.refreshCheckout();
    isLoading.value = false;
  }
};
</script>
